import React from "react";
import { Router, Route, Switch } from "react-router-dom";
import { createBrowserHistory as bHistory } from "history";

import UnsuccessfulPaymentPage from "app/pages/payment/UnsuccessfulPaymentPage";
import SuccessfulPaymentPage from "app/pages/payment/SuccessfulPaymentPage";
import UploadEmployeesScreen from "app/pages/UploadEmployeesScreen";
import RecurrentPaymentsScreen from "app/pages/RecurrentPaymentsScreen";
import RecurrentPaymentDetailScreen from "app/pages/RecurrentPaymentsScreen/subPages/RecurrentPaymentDetailScreen";
import Playground from "app/components/Playground";

// Routes
import { displayEmployeesEditSummaryPage } from "usecases/employees/displays/display-employees-edit-summary";
import { Website } from "usecases/Website";
import { displayMyAccountPage } from "usecases/my-account/display-my-account-page";
import { displayConfigurationPage } from "usecases/configuration/display-configuration-page";
import {
  displaySubsidyPage,
  displaySubsidyPageById,
} from "usecases/benefit-model/displays/allowance/display-subsidy-page";
import { displayBenefitPage } from "usecases/benefit-model/displays/display-benefit-page";
import showAnalytics from "usecases/analytics/show-analytics";

import RequestsPage from "v2/ui/BenefitRequests/pages/RequestsPage";
import RequestDetailPage from "v2/ui/BenefitRequests/pages/RequestsDetailPage";
import QueryProvider from "v2/ui/Providers/QueryProvider";
import { CobeeProvider } from "@cobee/cobee-ui";
import theme from "v2/infrastructure/common/theme";
import { SplitFactory } from "@splitsoftware/splitio-react";
import { Permission } from "v2/domain/common/entities/ManagerPermission/ManagerPermission";
import EditEmployeesCsvPageWrapper from "usecases/employees/components/editEmployeesCsvPage/EditEmployeesCsvPageWrapper";
import Page from "./Page";
import PrivateRoute from "./PrivateRoute";
import auth0Client from "../auth/Auth";
import AppLayout from "../../v2/ui/components/AppLayout";
import sdkConfig from "../../infrastructure/split/sdkConfig";
import reviewEmployeesToDelete from "../../usecases/employees/review-employees-to-delete";

import { Routes as RoutesEnum } from "./config";
import { displayEmployeeCurrentMonthPurchases } from "../../usecases/employee-detail/displays/display-employee-current-month-purchases";
import consultEmployeeRequests from "../../usecases/employees/consult-employee-requests";
import IndividualDelivery from "../../ui/pages/IndividualDelivery";
import ProtectedRoute from "./ProtectedRoute";

const history = bHistory();

export function redirect(path) {
  history.push(path);
  history.go(0);
}

function Routes() {
  return (
    <SplitFactory config={sdkConfig}>
      <Router history={history}>
        <Switch>
          <PrivateRoute
            history={history}
            path="/"
            exact
            render={() => {
              window.location.href = `${window.location.protocol}//${window.location.host}/v2/welcome`;
              return null;
            }}
          />

          <PrivateRoute
            history={history}
            path="/my-account"
            exact
            render={() => {
              window.location.href = `${window.location.protocol}//${window.location.host}/v2`;
              return null;
            }}
          />

          <PrivateRoute
            history={history}
            path="/employees"
            render={() => {
              window.location.href = `${window.location.protocol}//${window.location.host}${RoutesEnum.EMPLOYEES_V2_WITH_PARAMS}`;
              return null;
            }}
          />

          <PrivateRoute
            history={history}
            path="/benefits/individual-benefit-delivery"
            render={() => (
              <QueryProvider>
                <IndividualDelivery />
              </QueryProvider>
            )}
          />

          <PrivateRoute
            history={history}
            path="/massive-employee-offboarding"
            render={() => (
              <Website
                onMount={async () => {
                  reviewEmployeesToDelete({
                    currentStep: 0,
                    employees: [],
                    isLoading: false,
                    target: "page-container",
                  });
                }}
              />
            )}
          />

          <PrivateRoute
            history={history}
            path="/edit-employees-by-csv"
            render={() => (
              <>
                <div id="modal-container" />
                <EditEmployeesCsvPageWrapper />
              </>
            )}
          />

          <PrivateRoute
            history={history}
            path="/employees-summary-edit"
            render={() => (
              <Website onMount={() => displayEmployeesEditSummaryPage({})} />
            )}
          />

          <PrivateRoute
            history={history}
            path="/upload-employees"
            render={() => (
              <>
                <div id="modal-container" />
                <QueryProvider>
                  <CobeeProvider theme={theme}>
                    <UploadEmployeesScreen />
                  </CobeeProvider>
                </QueryProvider>
              </>
            )}
          />

          <PrivateRoute
            history={history}
            path="/my-account"
            render={() => <Website onMount={displayMyAccountPage} />}
          />

          <PrivateRoute
            history={history}
            path="/benefits"
            exact
            render={() => <Website onMount={displayBenefitPage} />}
          />

          <PrivateRoute
            history={history}
            path="/benefits/recurrent-payments/:type/:id"
            render={() => (
              <CobeeProvider theme={theme}>
                <div id="modal-container" />
                <RecurrentPaymentDetailScreen />
              </CobeeProvider>
            )}
          />

          <PrivateRoute
            history={history}
            path="/benefits/recurrent-payments/:type"
            render={() => (
              <QueryProvider>
                <CobeeProvider theme={theme}>
                  <div id="modal-container" />
                  <RecurrentPaymentsScreen />
                </CobeeProvider>
              </QueryProvider>
            )}
          />

          <PrivateRoute
            history={history}
            path="/v2/requests/detail/:requestId"
            render={() => (
              <QueryProvider>
                <CobeeProvider theme={theme}>
                  <AppLayout>
                    <RequestDetailPage />
                  </AppLayout>
                </CobeeProvider>
              </QueryProvider>
            )}
          />

          <PrivateRoute
            history={history}
            path="/v2/employee-requests"
            render={() => (
              <QueryProvider>
                <CobeeProvider theme={theme}>
                  <AppLayout>
                    <ProtectedRoute
                      permissions={[Permission.CAN_ACCESS_EMPLOYEES_SECTION]}
                    >
                      <RequestsPage />
                    </ProtectedRoute>
                  </AppLayout>
                </CobeeProvider>
              </QueryProvider>
            )}
          />

          <PrivateRoute
            history={history}
            path="/benefits/:modelId/subsidy"
            render={(props) => (
              <Website
                onMount={() =>
                  displaySubsidyPageById({
                    modelId: props.match.params.modelId,
                    alertId: (props.location.search.split(
                      "?fromNotification="
                    ) || [null, null])[1],
                  })
                }
              />
            )}
          />

          <PrivateRoute
            history={history}
            path="/configuration"
            render={() => <Website onMount={displayConfigurationPage} />}
          />

          <PrivateRoute
            history={history}
            path="/analytics"
            render={() => <Website onMount={showAnalytics} />}
          />

          <PrivateRoute
            history={history}
            path="/subsidy"
            render={() => <Website onMount={displaySubsidyPage} />}
          />

          <PrivateRoute
            history={history}
            path="/successful-payment"
            render={(props) =>
              Page({
                component: SuccessfulPaymentPage,
                props,
              })
            }
          />

          <PrivateRoute
            history={history}
            path="/unsuccessful-payment"
            render={(props) =>
              Page({
                component: UnsuccessfulPaymentPage,
                props,
              })
            }
          />

          <PrivateRoute
            history={history}
            path="/employee-detail/:id/current-month-consumptions"
            exact
            render={(props) => (
              <Website
                onMount={() =>
                  displayEmployeeCurrentMonthPurchases({
                    employeeId: props.match.params.id,
                  })
                }
              />
            )}
          />

          <PrivateRoute
            history={history}
            path="/employee-detail/:id/renting-payments"
            exact
            render={(props) => (
              <Website
                onMount={() =>
                  consultEmployeeRequests({
                    employeeId: props.match.params.id,
                    wrapped: true,
                  })
                }
              />
            )}
          />

          {process.env.NODE_ENV === "development" && (
            <PrivateRoute
              history={history}
              path="/playground"
              render={() => (
                <Website>
                  <Playground />
                </Website>
              )}
            />
          )}

          <Route
            path="/callback"
            render={() => {
              window.location.replace("/");
              return <></>;
            }}
          />

          <Route
            path="/logout"
            render={() => {
              auth0Client.checkSession();
              return <div />;
            }}
          />
        </Switch>
      </Router>
    </SplitFactory>
  );
}

export default Routes;
